/**
 * Limpeza de texto para o TinyMCE ("Limpar e unir linhas"), portada da extensao
 * "Limpador Asgard Pro" (v3.5) para rodar direto no editor, sem depender de extensao de navegador.
 *
 * Preserva `<hr class="separador-ato">` e o primeiro paragrafo centralizado com fonte 14pt
 * (cabecalho de certidao) durante as etapas de fusao de paragrafos. As regras especificas de
 * Atos/Certidao da extensao (fusao de titulo com .Section1, separacao de cabecalho
 * "CERTIDAO..."/"CODIGO NACIONAL DE MATRICULA...") nao foram portadas - ficam restritas a
 * limpeza generica de espacamento, pontuacao, paragrafos e formatacao inline.
 *
 * Excecao: com "Unir titulo ao texto com quebra" ligado, um paragrafo cujo conteudo e somente
 * negrito (`<p><strong>...</strong></p>`) - incluindo o cabecalho preservado - funde com o
 * paragrafo comum seguinte via `<br>`, reproduzindo o comportamento observado na extensao em
 * paginas de Atos (normalizeAtosMergedHtml, injected-cleaner_3.2.js), que roda depois da
 * marcacao de preservacao ja ter sido removida e por isso ignora essa protecao.
 */

const NBSP = String.fromCharCode(160);
const ZWSP = String.fromCharCode(8203);
const BLANK_CHARS_REGEX = new RegExp("[\\s" + NBSP + ZWSP + "]+", "g");
const BLANK_CHARS_START_REGEX = new RegExp("^[\\s" + NBSP + ZWSP + "]+");
const BLANK_CHARS_END_REGEX = new RegExp("[\\s" + NBSP + ZWSP + "]+$");

const DEFAULT_PREFS = {
  justify: true,
  cleanSpaces: true,
  fixPunctuation: true,
  mergeLines: true,
  noNested: true,
  tightenInlineSpaces: true
};

const STORAGE_KEY = 'asgardLimparEUnirLinhasPrefs';

/**
 * Le as preferencias salvas no navegador do usuario. Cai para o padrao (tudo ligado) se nunca foi salvo antes,
 * ou se o localStorage estiver indisponivel (modo privado, quota, etc.).
 */
function getPrefs() {
  let stored = {};
  try {
    stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    stored = {};
  }
  return Object.assign({}, DEFAULT_PREFS, stored);
}

function setPref(key, value) {
  const prefs = getPrefs();
  prefs[key] = value;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    // localStorage indisponivel - a preferencia so vale para esta execucao
  }
  return prefs;
}

function tightenInlineSpaces(text) {
  return text
    .replace(/R\$\s+(?=\d)/g, 'R$')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')');
}

function createTextNode(doc, node, opt) {
  let text = node.nodeValue.replace(/[\n\r\t]/g, '').split(NBSP).join(' ');
  if (opt.fixPunctuation) text = text.replace(/\s+([.,:;])/g, '$1');
  if (opt.cleanSpaces) text = text.replace(/\s{2,}/g, ' ');
  if (opt.tightenInlineSpaces) text = tightenInlineSpaces(text);
  return doc.createTextNode(text);
}

function createParagraphNode(doc, opt) {
  const paragraph = doc.createElement('p');
  if (opt.justify) paragraph.style.textAlign = 'justify';
  paragraph.style.margin = '0 0 8px 0';
  paragraph.style.padding = '0px';
  return paragraph;
}

function createInlineWrapper(doc, tag, style) {
  const isBold = tag === 'STRONG' || tag === 'B' || /bold|700/.test(style);
  const isUnderline = tag === 'U' || style.includes('underline');
  const isItalic = tag === 'EM' || tag === 'I' || style.includes('italic');

  let wrapper = null;
  let innerest = null;

  const addTag = (tagName) => {
    const element = doc.createElement(tagName);
    if (!wrapper) {
      wrapper = element;
      innerest = element;
    } else {
      innerest.appendChild(element);
      innerest = element;
    }
  };

  if (isBold) addTag('strong');
  if (isUnderline) addTag('u');
  if (isItalic) addTag('em');

  return {wrapper: wrapper, innerest: innerest};
}

function preserveSeparator(doc, node) {
  if (node.tagName.toUpperCase() !== 'HR' || !node.classList.contains('separador-ato')) return null;

  const hr = doc.createElement('hr');
  hr.className = 'separador-ato';
  return hr;
}

function preserveFirstCenteredParagraph(node, state) {
  if (state.firstCenterPreserved) return null;
  if (node.tagName.toUpperCase() !== 'P') return null;
  if (node.style.textAlign !== 'center' || node.style.fontSize !== '14pt') return null;

  state.firstCenterPreserved = true;
  const clone = node.cloneNode(true);
  clone.dataset.asgardPreserve = 'true';
  return clone;
}

function mergeTitleParagraphs(doc, tempDiv) {
  Array.from(tempDiv.querySelectorAll('p')).forEach((paragraph) => {
    if (!paragraph.isConnected || paragraph.dataset.asgardPreserve) return;

    const text = paragraph.textContent.trim();
    if (!text || text.length >= 65) return;
    if (!paragraph.querySelector('strong, b')) return;

    let nextEl = paragraph.nextElementSibling;

    if (nextEl && nextEl.tagName === 'P' && !nextEl.dataset.asgardPreserve && !nextEl.textContent.trim()) {
      nextEl.remove();
      nextEl = paragraph.nextElementSibling;
    }

    if (!nextEl || nextEl.tagName !== 'P' || nextEl.dataset.asgardPreserve) return;
    if (!nextEl.textContent.trim()) return;

    let sibling = paragraph.nextSibling;
    while (sibling && sibling !== nextEl) {
      if (sibling.nodeName === 'HR') return;
      sibling = sibling.nextSibling;
    }

    paragraph.appendChild(doc.createElement('br'));
    while (nextEl.firstChild) {
      const child = nextEl.firstChild;
      if (child.nodeName === 'BR' || (child.nodeType === 3 && !child.nodeValue.trim())) {
        nextEl.removeChild(child);
      } else {
        paragraph.appendChild(child);
      }
    }

    nextEl.remove();
    paragraph.dataset.asgardMerged = 'true';
  });
}

function mergeParagraphs(doc, tempDiv) {
  const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
  let index = 0;

  while (index < paragraphs.length - 1) {
    const current = paragraphs[index];
    const next = paragraphs[index + 1];

    if (!current.isConnected || !next.isConnected) {
      index += 1;
      continue;
    }

    if (current.dataset.asgardPreserve || next.dataset.asgardPreserve) {
      index += 1;
      continue;
    }

    if (current.parentNode === next.parentNode) {
      let sibling = current.nextSibling;
      let hasSeparator = false;

      while (sibling && sibling !== next) {
        if (sibling.nodeName === 'HR' && sibling.classList.contains('separador-ato')) {
          hasSeparator = true;
          break;
        }
        sibling = sibling.nextSibling;
      }

      if (hasSeparator) {
        index += 1;
        continue;
      }
    }

    const isShort = current.textContent.trim().length < 65;
    const hasBold = current.querySelector('strong, b');

    if (isShort || hasBold) {
      const nextHasContent = next.textContent.trim().length > 0;
      if (nextHasContent) current.appendChild(doc.createElement('br'));

      while (next.firstChild) {
        const child = next.firstChild;
        if (child.nodeName === 'BR' || (child.nodeType === 3 && !child.nodeValue.trim())) {
          next.removeChild(child);
        } else {
          current.appendChild(child);
        }
      }

      next.remove();
      paragraphs.splice(index + 1, 1);
    } else {
      index += 1;
    }
  }
}

function cleanupBreaks(tempDiv) {
  tempDiv.querySelectorAll('p').forEach((paragraph) => {
    if (paragraph.dataset.asgardPreserve) return;

    while (paragraph.lastChild && paragraph.lastChild.nodeName === 'BR') {
      paragraph.removeChild(paragraph.lastChild);
    }
  });
}

function isBlankNode(node) {
  return node.nodeName === 'BR' ||
    (node.nodeType === 3 && !node.nodeValue.replace(BLANK_CHARS_REGEX, '')) ||
    (node.nodeType === 1 && node.nodeName !== 'HR' && !node.textContent.replace(BLANK_CHARS_REGEX, ''));
}

function stripEdgeBlankNodes(container) {
  while (container.firstChild && isBlankNode(container.firstChild)) {
    container.removeChild(container.firstChild);
  }

  while (container.lastChild && isBlankNode(container.lastChild)) {
    container.removeChild(container.lastChild);
  }
}

function trimEdgesFromNode(node) {
  if (!node) return;

  let firstText = node.firstChild;
  while (firstText && firstText.nodeType !== 3) {
    firstText = firstText.nextSibling;
  }
  if (firstText) {
    firstText.nodeValue = firstText.nodeValue.replace(BLANK_CHARS_START_REGEX, '');
    if (!firstText.nodeValue) firstText.remove();
  }

  let lastText = node.lastChild;
  while (lastText && lastText.nodeType !== 3) {
    lastText = lastText.previousSibling;
  }
  if (lastText) {
    lastText.nodeValue = lastText.nodeValue.replace(BLANK_CHARS_END_REGEX, '');
    if (!lastText.nodeValue) lastText.remove();
  }
}

function stripEdgeBlankParagraphsFromHtml(html) {
  const blankParagraphPattern = '(?:<p\\b[^>]*>(?:\\s|' + NBSP + '|' + ZWSP + '|&nbsp;|&#160;|<br\\s*\\/?>|<strong>\\s*<\\/strong>|<u>\\s*<\\/u>|<em>\\s*<\\/em>)*<\\/p>)';
  return html
    .replace(new RegExp('^(?:\\s|' + blankParagraphPattern + ')+', 'i'), '')
    .replace(new RegExp('(?:\\s|' + blankParagraphPattern + ')+$', 'i'), '');
}

const BOLD_ONLY_TITLE_MERGE_REGEX = new RegExp(
  '<p([^>]*)>\\s*<strong>([\\s\\S]*?)<\\/strong>\\s*<\\/p>\\s*' +
  '(?:<p[^>]*>(?:\\s|&nbsp;|&#160;|' + NBSP + '|' + ZWSP + '|<br\\s*\\/?>)*<\\/p>\\s*)?' +
  '<p([^>]*)>([\\s\\S]*?)<\\/p>',
  'i'
);

/**
 * Funde um paragrafo cujo conteudo e somente negrito (`<p><strong>...</strong></p>`) com o
 * paragrafo comum seguinte via `<br>` - ignora tamanho e a marcacao de preservacao, ja removida
 * a essa altura do pipeline. So substitui a primeira ocorrencia no documento (mesma limitacao da
 * funcao original da extensao, que usa replace sem flag global).
 */
function mergeBoldOnlyTitleWithBodyHtml(html) {
  return html.replace(BOLD_ONLY_TITLE_MERGE_REGEX, function (match, titleAttrs, titleInner, bodyAttrs, bodyInner) {
    const trimmedTitle = titleInner.replace(/\s+$/, '');
    return '<p' + titleAttrs + '><strong>' + trimmedTitle + '<br /></strong>' + bodyInner + '</p>';
  });
}

function hasMeaningfulContent(target) {
  if (!target) return false;

  const text = (target.textContent || '').replace(BLANK_CHARS_REGEX, '');
  if (text) return true;

  const html = (target.innerHTML || '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;|&#160;/gi, '')
    .replace(/\s+/g, '');

  return html !== '';
}

function buildCleanTree(doc, body, opt) {
  const state = {firstCenterPreserved: false};
  const tempDiv = doc.createElement('div');

  function getCleanNode(node) {
    if (node.nodeType === 3) {
      return createTextNode(doc, node, opt);
    }

    if (node.nodeType !== 1) return null;

    const tag = node.tagName.toUpperCase();

    if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'META' || tag === 'LINK') return null;

    const preservedSeparator = preserveSeparator(doc, node);
    if (preservedSeparator) return preservedSeparator;

    const preservedParagraph = preserveFirstCenteredParagraph(node, state);
    if (preservedParagraph) return preservedParagraph;

    const style = (node.getAttribute('style') || '').toLowerCase();
    const inlineWrapper = createInlineWrapper(doc, tag, style);
    if (inlineWrapper.wrapper) {
      Array.from(node.childNodes).forEach((child) => {
        const cleanedChild = getCleanNode(child);
        if (cleanedChild) inlineWrapper.innerest.appendChild(cleanedChild);
      });
      return inlineWrapper.wrapper;
    }

    if (tag === 'P' || tag === 'DIV') {
      const paragraph = createParagraphNode(doc, opt);
      Array.from(node.childNodes).forEach((child) => {
        const cleanedChild = getCleanNode(child);
        if (cleanedChild) paragraph.appendChild(cleanedChild);
      });
      return paragraph;
    }

    if (tag === 'BR') return doc.createElement('br');

    const fragment = doc.createDocumentFragment();
    Array.from(node.childNodes).forEach((child) => {
      const cleanedChild = getCleanNode(child);
      if (cleanedChild) fragment.appendChild(cleanedChild);
    });
    return fragment;
  }

  Array.from(body.childNodes).forEach((node) => {
    const cleanedNode = getCleanNode(node);
    if (cleanedNode) tempDiv.appendChild(cleanedNode);
  });

  return tempDiv;
}

/**
 * Limpa e une as linhas do conteudo do editor: normaliza espacos/pontuacao, justifica paragrafos,
 * une titulo curto em negrito ao paragrafo seguinte e remove tags inline repetidas - preservando
 * `<hr class="separador-ato">` e o primeiro paragrafo centralizado com fonte 14pt, se existirem.
 *
 * Com "Unir titulo ao texto com quebra" ligado, um paragrafo inteiramente em negrito (qualquer
 * tamanho, incluindo o cabecalho preservado) tambem funde com o proximo paragrafo comum - ver
 * mergeBoldOnlyTitleWithBodyHtml.
 *
 * Roda dentro de um undoManager.transact() para que um Ctrl+Z desfaca a limpeza inteira de uma vez.
 */
function limparEUnirLinhas(editor) {
  const doc = editor.getDoc();
  const body = editor.getBody();

  if (!hasMeaningfulContent(body)) return false;

  const opt = getPrefs();

  editor.undoManager.transact(() => {
    const tempDiv = buildCleanTree(doc, body, opt);

    stripEdgeBlankNodes(tempDiv);

    const allPs = tempDiv.querySelectorAll('p');
    if (allPs.length > 0) {
      const lastP = allPs[allPs.length - 1];
      while (lastP.lastChild && lastP.lastChild.nodeName === 'BR') {
        lastP.removeChild(lastP.lastChild);
      }
    }

    if (opt.noNested) {
      ['strong', 'u', 'em'].forEach((tagName) => {
        let nested;
        while ((nested = tempDiv.querySelector(tagName + ' ' + tagName)) !== null) {
          while (nested.firstChild) nested.parentNode.insertBefore(nested.firstChild, nested);
          nested.parentNode.removeChild(nested);
        }
      });
    }

    mergeTitleParagraphs(doc, tempDiv);

    if (opt.mergeLines) {
      mergeParagraphs(doc, tempDiv);
    }

    cleanupBreaks(tempDiv);

    tempDiv.querySelectorAll('[data-asgard-preserve]').forEach((element) => {
      element.removeAttribute('data-asgard-preserve');
    });

    if (opt.cleanSpaces) {
      trimEdgesFromNode(tempDiv);
    }

    while (tempDiv.firstChild && tempDiv.firstChild.nodeName === 'BR') {
      tempDiv.removeChild(tempDiv.firstChild);
    }

    let finalHtml = stripEdgeBlankParagraphsFromHtml(tempDiv.innerHTML);

    if (opt.mergeLines) {
      finalHtml = mergeBoldOnlyTitleWithBodyHtml(finalHtml);
    }

    editor.setContent(finalHtml);
  });

  editor.fire('change');
  return true;
}

export default {
  limparEUnirLinhas,
  getPrefs,
  setPref
};
